import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortDownArrow } from "../../assets/svg";
import Action from "../../components/common/Action";
import Breadcrumb from "../../components/common/BreadCrumb";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customTable/CustomTable";
import RippleLoader from "../../components/loader/RippleLoader";
import { AddButton } from "../../components/micro/input";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCategories,
  getCategoryList,
} from "../../redux/categories/slice";

const ProductCategory = () => {
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [categoryDelete, setCategoryDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // selector
  const { allCategoriesList, allCategoriesLoading } = useSelector((store) => ({
    // all RoleL ist
    allCategoriesList: store?.categoriesData?.getCategoriesListStatus?.data,
    allCategoriesLoading: store?.categoriesData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getCategoryList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        limit: pageLimit,
      })
    );
  }, [dispatch, searchValue, pageCount, pageLimit, sortBy, sortDirection]);

  const deleteCategoryId = () => {
    setIsLoading(true);
    if (!categoryDelete) return;
    dispatch(deleteCategories(categoryDelete)).then((res) => {
      if (res.type === "deleteCategories/fulfilled") {
        dispatch(
          getCategoryList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
            limit: pageLimit,
          })
        );
      }
      setIsLoading(false);
      setConfirmDelete(false);
    });
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        No.
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Category</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "name" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("products_count");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Number of Items</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "products_count"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Action
      </th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(allCategoriesList?.data?.data || [])?.map((element, index) => (
        <tr
          key={element.id}
          className="font-Proxima shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-5 py-3 truncate">{element?.id}</td>
          <td className="px-5 py-3 truncate">
            {element?.name
              ? element?.name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3 truncate">{element?.products_count}</td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/create-edit-categories", {
                        state: { category: element, mode: "edit" },
                      })
                    }
                  >
                    View/Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setCategoryDelete(element?.id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-7">
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              Product Category
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Product Category"}
              breadCrumbParent2={"Category Management"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
          <AddButton
            value="Add Category"
            onClick={() =>
              navigate("/create-edit-categories", {
                state: { mode: "create" },
              })
            }
          />
        </div>
        <div className="flex items-center space-x-3">
          <DebounceSearch
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
        </div>

        {allCategoriesLoading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg">
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={allCategoriesList?.data}
              overflowVisibleLg={true}
              // loader
              loading={false}
              showPagination={true}
              // showLimit
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              // paginationData
              total={allCategoriesList?.data?.total}
              current={pageCount}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(allCategoriesList?.data?.total / pageLimit)}
            />
          </div>
        )}
      </div>
      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteCategoryId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default ProductCategory;
